import { FALLBACK_CURRENCY, numberOr } from "../checkout/money";
import { formatOrderDate, formatOrderTime } from "../orders/format";
import { moneyOf, signMoney } from "./financeMoney";

const GATEWAY_LABELS = {
  PAYSTACK: "Card",
  WALLET: "Wallet",
  COD: "Cash on Delivery",
};

const KIND_TITLES = {
  TOP_UP: "Wallet Top-Up",
  PURCHASE: "Order Payment",
  REFUND: "Refund",
  OTHER: "Transaction",
};

const STATUS_LABELS = {
  CLAIMED: "Completed",
  COMPLETED: "Completed",
  PENDING_CLAIM: "Pending",
  VOIDED: "Voided",
  FAILED: "Failed",
};

const STATES = {
  CLAIMED: "SETTLED",
  COMPLETED: "SETTLED",
  PENDING_CLAIM: "PENDING",
  VOIDED: "FAILED",
  FAILED: "FAILED",
};

export const TRANSACTION_PURPOSES = {
  TOP_UP: "wallet.topup",
  PURCHASE: "order.purchase",
};

const upper = (value) => String(value ?? "").trim().toUpperCase();

const titleCase = (value) =>
  String(value ?? "")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const kindOf = (purpose) => {
  const code = String(purpose ?? "").trim().toLowerCase();

  if (code === "wallet.topup") return "TOP_UP";
  if (code === "order.purchase") return "PURCHASE";
  if (code === "order.refund" || code === "refund") return "REFUND";

  return "OTHER";
};

const impactOf = (raw, kind) => {
  const code = upper(raw);

  if (code === "C" || code === "CREDIT") return "C";
  if (code === "D" || code === "DEBIT") return "D";

  return kind === "TOP_UP" || kind === "REFUND" ? "C" : "D";
};

const mapCard = (dto) => {
  const last4 = dto?.last4 ?? dto?.last_4 ?? null;

  if (!last4) return null;

  const brand = dto?.brand ?? null;

  return {
    brand,
    last4: String(last4),
    label: `${brand ? titleCase(brand) : "Card"} ···· ${last4}`,
  };
};

const mapFx = (dto) => {
  if (!dto) return null;

  const rate = numberOr(dto?.conversionRate);
  const requestCurrency = upper(dto?.requestCurrency) || null;
  const billingCurrency = upper(dto?.billingCurrency) || null;
  const requested = numberOr(dto?.requestedAmount);
  const billed = numberOr(dto?.billingAmount);

  if (rate === null && requested === null && billed === null) return null;

  return {
    rate,
    requested: requested === null ? null : moneyOf(requested, requestCurrency),
    billed: billed === null ? null : moneyOf(billed, billingCurrency),
    balanceBefore:
      numberOr(dto?.balanceBefore) === null
        ? null
        : moneyOf(dto.balanceBefore, billingCurrency),
    balanceAfter:
      numberOr(dto?.balanceAfter) === null
        ? null
        : moneyOf(dto.balanceAfter, billingCurrency),
    converted: rate !== 1 || requestCurrency !== billingCurrency,
  };
};

const mapOrderSummary = (dto) => {
  const orderId = numberOr(dto?.orderId);

  if (orderId === null) return null;

  const volume = numberOr(dto?.volume);
  const unit = dto?.unit || "Liters";
  const volumeLabel = volume === null ? null : `${volume} ${unit}`;
  const productSummary = dto?.productSummary || null;
  const details = [productSummary, volumeLabel].filter(Boolean);

  return {
    orderId,
    supplierId: numberOr(dto?.supplierId),
    supplierName: dto?.supplierName || null,
    productSummary,
    volume,
    unit,
    volumeLabel,
    detailLabel: details.length ? details.join(", ") : null,
  };
};

export const mapTransaction = (dto) => {
  const id = numberOr(dto?.id);

  if (id === null) return null;

  const purpose = dto?.purpose ?? null;
  const kind = kindOf(purpose);
  const impact = impactOf(dto?.impact, kind);
  const gateway = upper(dto?.gateway) || null;
  const status = upper(dto?.status) || null;
  const createdAt = dto?.createdAt ?? null;
  const amount = moneyOf(dto?.amount, upper(dto?.currencyCode));
  const order = mapOrderSummary(dto?.orderSummary);
  const description = String(dto?.description ?? "").trim();

  return {
    id,
    createdAt,
    dateLabel: createdAt ? formatOrderDate(createdAt) || null : null,
    timeLabel: createdAt ? formatOrderTime(createdAt) || null : null,
    gateway,
    gatewayLabel: GATEWAY_LABELS[gateway] || "Payment",
    purpose,
    purposeLabel: KIND_TITLES[kind],
    kind,
    title: description || KIND_TITLES[kind],
    amount,
    impact,
    signedAmount: signMoney(amount, impact),
    status,
    state: STATES[status] || "PENDING",
    statusLabel: status ? STATUS_LABELS[status] || titleCase(status) : null,
    orderId: numberOr(dto?.additionalData?.orderId) ?? order?.orderId ?? null,
    card: mapCard(dto?.paystackTransaction),
    fx: mapFx(dto?.walletTransaction),
    order,
  };
};

export const mapTransactions = (list) => (list || []).map(mapTransaction).filter(Boolean);

export const maskAccountNumber = (raw) => {
  const value = String(raw ?? "").trim();

  if (!value) return "";
  if (value.includes("*")) return value;
  if (value.length <= 4) return value;

  return `**** **** ${value.slice(-4)}`;
};

export const recipientType = (dto) => {
  const type = upper(dto?.type);

  if (type.includes("MOBILE") || type.includes("MOMO")) return "MOBILE_MONEY";
  if (type.includes("BANK")) return "BANK";
  if (dto?.operatorCode || dto?.operatorName) return "MOBILE_MONEY";

  return "UNKNOWN";
};

export const recipientProviderName = (dto) =>
  dto?.bankName ||
  dto?.operatorName ||
  dto?.bankCode ||
  dto?.operatorCode ||
  (recipientType(dto) === "MOBILE_MONEY" ? "Mobile Money" : "Bank Account");

export const recipientLabel = (dto) =>
  `${recipientProviderName(dto)} · ${maskAccountNumber(dto?.accountNumber)}`;

export const mapTransferRecipient = (dto) => {
  const id = dto?.id ?? null;

  if (id === null || id === undefined) return null;

  return {
    id,
    type: recipientType(dto),
    accountName: dto?.accountName || "",
    accountNumber: dto?.accountNumber || "",
    maskedAccountNumber: maskAccountNumber(dto?.accountNumber),
    providerName: recipientProviderName(dto),
    label: recipientLabel(dto),
    currency: upper(dto?.currency) || FALLBACK_CURRENCY,
    isDefault: Boolean(dto?.isDefault ?? dto?.default),
  };
};

export const mapTransferRecipients = (list) =>
  (list || []).map(mapTransferRecipient).filter(Boolean);

const PAYOUT_STATUSES = {
  PAID: "PAID",
  COMPLETED: "PAID",
  SETTLED: "PAID",
  SUCCESS: "PAID",
  APPROVED: "IN_PROGRESS",
  PROCESSING: "IN_PROGRESS",
  IN_PROGRESS: "IN_PROGRESS",
  SENT: "IN_PROGRESS",
  PENDING: "PENDING",
  REQUESTED: "PENDING",
  REJECTED: "REJECTED",
  FAILED: "REJECTED",
  CANCELLED: "REJECTED",
  CANCELED: "REJECTED",
};

export const PAYOUT_STATUS_LABELS = {
  PAID: "Paid",
  IN_PROGRESS: "In Progress",
  PENDING: "Pending",
  REJECTED: "Rejected",
};

export const payoutStatus = (dto) => {
  const code = upper(dto?.status).replace(/[\s-]+/g, "_");
  const mapped = PAYOUT_STATUSES[code];

  if (mapped) return mapped;

  if (dto?.rejected) return "REJECTED";
  if (dto?.paid) return "PAID";
  if (dto?.approved) return "IN_PROGRESS";

  return "PENDING";
};

const readMoney = (raw, currency) => {
  if (raw === null || raw === undefined) return null;

  if (typeof raw === "object") {
    const value = numberOr(raw?.value);

    return value === null ? null : moneyOf(value, raw?.currency || currency);
  }

  const value = numberOr(raw);

  return value === null ? null : moneyOf(value, currency);
};

export const toFeeQuote = (dto, { value, currency } = {}) => {
  const ccy = upper(dto?.currency) || currency || FALLBACK_CURRENCY;
  const gross = moneyOf(value, ccy);
  const fee = readMoney(dto?.feeAmount ?? dto?.fee, ccy) || moneyOf(0, ccy);
  const net =
    readMoney(dto?.netAmount ?? dto?.net, ccy) ||
    moneyOf(Math.max(gross.value - fee.value, 0), ccy);

  return { gross, fee, net, currency: ccy };
};

export const mapPayout = (dto) => {
  const id = dto?.id ?? null;

  if (id === null || id === undefined) return null;

  const status = payoutStatus(dto);
  const currency = upper(dto?.currency) || upper(dto?.amount?.currency) || FALLBACK_CURRENCY;
  const timestamp =
    dto?.paidAt ?? dto?.rejectedAt ?? dto?.approvedAt ?? dto?.requestedAt ?? dto?.createdAt ?? null;

  return {
    id,
    reference: dto?.requestNumber || String(id),
    status,
    statusLabel: PAYOUT_STATUS_LABELS[status],
    amount: readMoney(dto?.amount, currency) || moneyOf(0, currency),
    feeAmount: readMoney(dto?.feeAmount ?? dto?.fee, currency),
    netAmount: readMoney(dto?.netAmount ?? dto?.net, currency),
    recipient: dto?.transferRecipient ? mapTransferRecipient(dto.transferRecipient) : null,
    recipientLabel: dto?.transferRecipient ? recipientLabel(dto.transferRecipient) : null,
    timestamp,
    dateLabel: timestamp ? formatOrderDate(timestamp) || null : null,
    timeLabel: timestamp ? formatOrderTime(timestamp) || null : null,
    requestedAt: dto?.requestedAt ?? dto?.createdAt ?? null,
    approvedAt: dto?.approvedAt ?? null,
    paidAt: dto?.paidAt ?? null,
    rejectedAt: dto?.rejectedAt ?? null,
    rejectionReason: dto?.rejectionReason || null,
    receiptUrl: dto?.receipt?.url || dto?.receiptUrl || null,
    canCancel: status === "PENDING",
  };
};

export const mapPayouts = (list) => (list || []).map(mapPayout).filter(Boolean);

export const mapBank = (dto) => ({
  code: dto?.code ?? dto?.bankCode ?? null,
  name: dto?.name ?? dto?.bankName ?? "",
});

export const mapOperator = (dto) => ({
  code: dto?.code ?? dto?.operatorCode ?? null,
  name: dto?.name ?? dto?.operatorName ?? "",
});
